import { Inject, Pipe } from '@angular/core';
import { CurrencyPipe as AngularCurrencyPipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { LocaleService } from '../services/locale/locale.service';
import { NumberFormatterService } from '../services/number/number-formatter.service';
import { BaseNumberPipe } from './base-number.pipe';
import type { PipeTransform } from '@angular/core';

import type { LocaleDetails } from '../services/locale/locale.domain';
import type { NumberPipeParameters, ShortenData } from './base-number.pipe';

export interface CurrencyPipeParameters extends NumberPipeParameters {
  currencyCode?: string;
  asHtml?: boolean;
}

/**
 * Formats number as currency, optionally appending currency code.
 * if asHtml parameter set to true returned number and currency code will be wrapped in the spans with classes applied to them.
 * td-wb-currency-value and td-wb-currency-code respectively.
 */
@Pipe({
  name: 'tdWbCurrency'
})
export class CurrencyPipe extends BaseNumberPipe implements PipeTransform {

  private static FORMATTED_CURRENCY_VALUE_CLASS: string = 'td-wb-currency-value';
  private static FORMATTED_CURRENCY_CODE_CLASS: string = 'td-wb-currency-code';

  private currencyPipe: AngularCurrencyPipe;

  constructor(@Inject(TranslateService) translateService: TranslateService, @Inject(LocaleService) private localeService: LocaleService) {
    super(translateService);
    this.currencyPipe = new AngularCurrencyPipe(localeService.getLocale() as string);
  }

  transform(data: number, pipeParameters?: CurrencyPipeParameters): string {
    return this.renderValue(data, BaseNumberPipe.applyParameterDefaults(pipeParameters, 'c'));
  }

  protected doFormat(val: number, format: string | undefined, precision: number | undefined): string {
    const adjustedPrecision: number = NumberFormatterService.getAdjustedPrecision(val, format, precision);
    let result: string = this.currencyPipe.transform(
      Math.abs(val),
      'CAD',
      'symbol-narrow',
      NumberFormatterService.getPrecisionString(adjustedPrecision));
    if (val < 0) {   // Move '-' sign before '$' sign on all negative values. non standard legacy behaviour
      result = `-${result}`;
    }
    return result;
  }

  protected postFormat(formattedValue: string, pipeParameters: CurrencyPipeParameters, shortenData?: ShortenData): string {
    let result: string = formattedValue;
    if (shortenData !== undefined && shortenData.rangeLabel !== undefined) {
      const localeDetails: LocaleDetails = this.localeService.getLocaleDetails();
      if (result.lastIndexOf(localeDetails.currencySymbol) === result.length - 1) { // French
        result = result.replace(localeDetails.currencySymbol, '').trim();
        result += `${shortenData.rangeLabel} ${localeDetails.currencySymbol}`;
      } else {
        result += shortenData.rangeLabel;
      }
    }
    if (pipeParameters.currencyCode !== undefined) {
      return this.prependCurrencySymbol(result, pipeParameters.currencyCode, pipeParameters.asHtml);
    }
    return result;
  }

  // prepend and translate currency. it's only needed because of the non-standard formatting rules we use
  private prependCurrencySymbol(formattedCurrency: string, currencyCode: string, wrapInHtml: boolean): string {
    let params: Record<string, unknown>;
    if (wrapInHtml) {
      params = {
        amount: BaseNumberPipe.getSpanWithText(formattedCurrency, CurrencyPipe.FORMATTED_CURRENCY_VALUE_CLASS),
        currencyCode: BaseNumberPipe.getSpanWithText(currencyCode, CurrencyPipe.FORMATTED_CURRENCY_CODE_CLASS)
      };
    } else {
      params = { amount: formattedCurrency, currencyCode };
    }
    return this.translateService.instant('tdWb.core.pipes.currency.CURRENCY_WITH_CODE', params);
  }

}
